// PointsToCashDetail.cshtml: allocate points to a cardholder, or redeem them as cash loaded onto the card.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { adminApi } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { confirmed, PageHeading, ResultBanner, StatusBadge } from '../components/ui';

const NOT_ENABLED = "This partner's program doesn't have points-to-cash redemption enabled.";
const n0 = (n: number) => n.toLocaleString('en-US');

export function PointsToCashDetailPage() {
  const { id = '' } = useParams();
  const client = useQueryClient();
  const data = useQuery({
    queryKey: ['points-detail', id],
    queryFn: async () => {
      const cardholder = await adminApi.cardholder(id);
      if (!cardholder) return { cardholder, points: null, enabled: true };
      const [points, disabled] = await Promise.all([
        adminApi.pointsBalance(id),
        adminApi.disabledFeatures(cardholder.programId),
      ]);
      return { cardholder, points, enabled: !disabled.includes('PointsToCash') };
    },
  });
  const cardholder = data.data?.cardholder ?? null;
  usePageTitle(cardholder?.name ?? 'Points to Cash');
  const [outcome, setOutcome] = useState<{ message: string | null; ok: boolean }>({ message: null, ok: false });
  const [allocate, setAllocate] = useState({ points: '', note: '' });
  const [redeem, setRedeem] = useState('');
  const [busy, setBusy] = useState(false);

  async function done(message: string, ok: boolean) {
    setOutcome({ message, ok });
    await client.invalidateQueries({ queryKey: ['points-detail', id] });
  }

  async function submitAllocate(e: FormEvent) {
    e.preventDefault();
    const points = Number.parseInt(allocate.points, 10);
    if (!(points > 0)) return done('Enter a positive number of points to allocate.', false);
    setBusy(true);
    const r = await adminApi.allocatePoints(id, points, allocate.note === '' ? null : allocate.note);
    setBusy(false);
    await done(r.success ? `${points} points allocated.` : (r.error ?? 'Could not allocate points.'), r.success);
  }

  async function submitRedeem(e: FormEvent) {
    e.preventDefault();
    if (!confirmed('Redeem these points and load the cash onto the card now?')) return;
    const points = Number.parseInt(redeem, 10);
    // the program may have switched it off since the page was shown
    const disabled = cardholder ? await adminApi.disabledFeatures(cardholder.programId) : [];
    if (disabled.includes('PointsToCash')) return done(NOT_ENABLED, false);
    if (!(points > 0)) return done('Enter a positive number of points to redeem.', false);
    setBusy(true);
    const r = await adminApi.redeemPoints(id, points);
    setBusy(false);
    await done(
      r.success ? `Redeemed ${points} points for ${r.cashAmount.toFixed(2)} ${r.currency}.` : r.message,
      r.success,
    );
  }

  if (data.isPending) return null;
  const name = cardholder?.name ?? 'Cardholder';
  const points = data.data?.points ?? null;

  return (
    <>
      <PageHeading title={name} trail={[['Points to Cash', '/points']]} />
      <ResultBanner {...outcome} />

      {!cardholder ? (
        <p className="qc-empty">Cardholder not found.</p>
      ) : (
        <div className="qc-detail-row">
          <div className="qc-panel">
            <div className="qc-panel-title">
              <h3>Cardholder</h3>
              <span>{cardholder.programName}</span>
            </div>
            <dl className="qc-detail-dl">
              <div>
                <dt>Email</dt>
                <dd>{cardholder.email}</dd>
              </div>
              <div>
                <dt>Processor</dt>
                <dd>{cardholder.provider}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <StatusBadge status={cardholder.status} />
                </dd>
              </div>
            </dl>

            {points && (
              <>
                <div className="qc-panel-title" style={{ marginTop: 20 }}>
                  <h3>Points balance</h3>
                </div>
                <dl className="qc-detail-dl">
                  <div>
                    <dt>Balance</dt>
                    <dd>{n0(points.balance)} points</dd>
                  </div>
                  <div>
                    <dt>Current rate</dt>
                    <dd>
                      {points.pointsPerUnit} points = 1.00 {points.currency}
                    </dd>
                  </div>
                </dl>

                <form onSubmit={(e) => void submitAllocate(e)} className="qc-points-rate-form">
                  <label className="qc-field">
                    <span>Allocate points</span>
                    <input
                      type="number"
                      name="AllocatePoints"
                      min="1"
                      step="1"
                      required
                      value={allocate.points}
                      onChange={(e) => setAllocate({ ...allocate, points: e.target.value })}
                    />
                  </label>
                  <label className="qc-field">
                    <span>Note (optional)</span>
                    <input
                      type="text"
                      name="AllocateNote"
                      maxLength={255}
                      placeholder="e.g. Q3 loyalty bonus"
                      value={allocate.note}
                      onChange={(e) => setAllocate({ ...allocate, note: e.target.value })}
                    />
                  </label>
                  <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                    Allocate
                  </button>
                </form>
              </>
            )}
          </div>

          <div className="qc-panel">
            <div className="qc-panel-title">
              <h3>Redeem to cash</h3>
              <span>Loads cash onto the cardholder&apos;s card</span>
            </div>
            {!data.data?.enabled ? (
              <p className="qc-empty">{NOT_ENABLED}</p>
            ) : !points ? (
              <p className="qc-empty">Could not load this cardholder&apos;s points balance.</p>
            ) : points.balance <= 0 ? (
              <p className="qc-empty">No points available to redeem yet.</p>
            ) : (
              <form onSubmit={(e) => void submitRedeem(e)} className="qc-points-rate-form">
                <label className="qc-field">
                  <span>Points to redeem (max {n0(points.balance)})</span>
                  <input
                    type="number"
                    name="RedeemPoints"
                    min="1"
                    max={points.balance}
                    step="1"
                    required
                    value={redeem}
                    onChange={(e) => setRedeem(e.target.value)}
                  />
                </label>
                <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                  Redeem now
                </button>
              </form>
            )}

            <div className="qc-panel-title" style={{ marginTop: 24 }}>
              <h3>History</h3>
            </div>
            {!points || points.history.length === 0 ? (
              <p className="qc-empty">No points activity yet.</p>
            ) : (
              <table className="qc-plain-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Points</th>
                    <th>Cash</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {points.history.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        <span
                          className={`qc-badge ${entry.type === 'Allocation' || entry.type === 'Earn' ? 'qc-badge-success' : 'qc-badge-muted'}`}
                        >
                          {entry.type}
                        </span>
                        {entry.note && entry.note.trim() !== '' && (
                          <div style={{ color: 'var(--qor-muted)', fontSize: '.8rem', marginTop: 4 }}>{entry.note}</div>
                        )}
                        {entry.reason && (
                          <div style={{ color: 'var(--qor-muted)', fontSize: '.8rem', marginTop: 4 }}>
                            {entry.reason}
                          </div>
                        )}
                      </td>
                      <td>
                        {entry.pointsDelta > 0 ? '+' : ''}
                        {n0(entry.pointsDelta)}
                      </td>
                      <td>{entry.cashAmount !== null ? `${entry.cashAmount.toFixed(2)} ${entry.currency}` : '--'}</td>
                      <td>{shortDate(entry.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}
